const HOSTS = new Set(['app.quotemedia.com', 'uat.quotemedia.com', 'quotes.quotemedia.com']);
const MAX_BYTES = 64 * 1024 * 1024;
const DAY = 86400000;
const HISTORY_FLOOR = '1900-01-01';
const formatters = new Map();
const oneOrMany = value => value == null ? [] : Array.isArray(value) ? value : [value];
const field = (value, key) => value && Object.entries(value).find(([name]) => name.toLowerCase() === key)?.[1];
const dateNumber = date => Date.parse(`${date}T00:00:00Z`);
const shift = (date, days) => new Date(dateNumber(date) + days * DAY).toISOString().slice(0, 10);

export function historyError(status = 502, reason = null) {
  const error = new Error(reason === 'provider_browser_unavailable'
    ? 'QuoteMedia browser access is unavailable. Check cross-origin access and retry the chart.'
    : 'QuoteMedia did not return usable chart history. Please retry.');
  return Object.assign(error, { name: 'MarketDataRequestError', status, reason });
}

export function validateHistoryGrant(grant) {
  if (grant?.transport !== 'Browser' || !grant.sid || !grant.webmasterId || !grant.providerSymbol ||
      !['RealTime', 'Delayed', 'Historical'].includes(grant.availability) ||
      !['1m', '5m', '15m', '1h', '1D'].includes(grant.sourceTimeframe) ||
      !Number.isInteger(grant.sourceCount) || grant.sourceCount < 10 || grant.sourceCount > 5000 ||
      !Number.isFinite(Date.parse(grant.expiresAtUtc)) || Date.parse(grant.expiresAtUtc) <= Date.now()) throw historyError();
  for (const [key, path] of [['fullHistoryEndpoint', '/data/getFullHistory.json'],
    ['intervalsEndpoint', '/data/getIntervals.json']]) {
    const url = new URL(grant[key]);
    const approvedBeta = key === 'intervalsEndpoint' && url.hostname === 'quotes.quotemedia.com' &&
      url.pathname === '/beta/getIntervals.json';
    if (url.protocol !== 'https:' || !HOSTS.has(url.hostname) || (url.pathname !== path && !approvedBeta) ||
        url.username || url.password || url.port || url.search || url.hash) throw historyError();
  }
  if ((new URL(grant.fullHistoryEndpoint).hostname === 'uat.quotemedia.com') !==
      (new URL(grant.intervalsEndpoint).hostname === 'uat.quotemedia.com')) throw historyError();
  for (const key of ['password', 'username', 'enterpriseToken', 'applicationSecret'])
    if (Object.hasOwn(grant, key)) throw historyError();
  localParts(Date.now(), grant.timeZoneId); // Reject an invalid exchange timezone before fetching.
}

export class QuoteMediaBrowserHistoryClient {
  async load(grant, request) {
    validateHistoryGrant(grant);
    const today = localParts(Date.now(), grant.timeZoneId).date;
    const daily = grant.sourceTimeframe === '1D';
    const latest = daily || grant.availability === 'Historical' ? shift(today, -1) : today;
    const end = request.endDate && request.endDate < latest ? request.endDate : latest;
    const beta = !daily && new URL(grant.intervalsEndpoint).pathname === '/beta/getIntervals.json';
    const maximumDays = beta ? 364 : 366;
    const intradayEarliest = shift(beta ? end : today, -maximumDays);
    const earliest = daily || (beta && intradayEarliest < HISTORY_FLOOR) ? HISTORY_FLOOR : intradayEarliest;
    const minutes = grant.sourceTimeframe === '1h' ? 60 : Number.parseInt(grant.sourceTimeframe, 10);
    let days = daily ? Math.ceil(grant.sourceCount * 366 / 250) + 14
      : Math.min(maximumDays, Math.ceil(grant.sourceCount * minutes / 390 * 7 / 5) + 7);
    let bars = [];
    let start = end;
    if (end >= earliest) {
      while (true) {
        start = shift(end, -days);
        if (start < earliest) start = earliest;
        bars = await this.range(grant, daily, start, end, minutes, request.signal);
        if (bars.length >= grant.sourceCount || start === earliest) break;
        days = daily ? Math.ceil((dateNumber(end) - dateNumber(earliest)) / DAY) : Math.min(maximumDays, days * 2);
      }
    }
    bars = bars.slice(-grant.sourceCount);
    if (daily && grant.currentBackfill && !request.endDate && request.includeCurrent !== false &&
        grant.availability !== 'Historical') {
      try {
        const current = (await this.range(grant, false, today, today, 60, request.signal)).filter(bar => {
          const local = localParts(Date.parse(bar.timestampUtc), grant.timeZoneId);
          return local.date === today && local.minute >= 570 && local.minute < 960;
        });
        if (current.length) bars = [...bars, combine(current, exchangeMidnight(today, grant.timeZoneId))]
          .slice(-grant.sourceCount);
      } catch (error) {
        if (request.signal?.aborted || error.reason === 'provider_auth_rejected') throw error;
        // Match the existing daily-history behavior during optional backfill outages.
      }
    }
    // A short beta year establishes coverage of this request window only.
    // Continue on the preceding date even when the provider returned no rows.
    const coverage = beta && bars.length < grant.sourceCount && earliest > HISTORY_FLOOR
      ? { exhausted: false, nextEndDate: shift(earliest, -1) } : null;
    return historyPage(bars, request.timeframe, grant, request.count, coverage);
  }

  async range(grant, daily, start, end, minutes, signal) {
    const parameters = daily ? { adjusted: 'true', unadjusted: 'false' }
      : { interval: minutes, marketSession: 'mkt', useSessionDates: 'true', includeNullIntervals: 'false',
        adjusted: 'false', unadjusted: 'true', timezone: 'true' };
    const payload = await providerRequest(grant, daily ? grant.fullHistoryEndpoint : grant.intervalsEndpoint,
      { ...parameters, symbol: grant.providerSymbol, start, end }, signal);
    return parseHistory(payload, grant, daily, start, end);
  }
}

async function providerRequest(grant, endpoint, parameters, signal) {
  const url = new URL(endpoint);
  for (const [key, value] of Object.entries({ ...parameters, webmasterId: grant.webmasterId, sid: grant.sid }))
    url.searchParams.set(key, value);
  if (grant.availability !== 'RealTime') url.searchParams.set('entmax', grant.availability === 'Delayed' ? 'DL' : 'EOD');
  try {
    const response = await fetch(url.href, { method: 'GET', mode: 'cors', credentials: 'omit',
      cache: 'no-store', referrerPolicy: 'no-referrer', headers: { Accept: 'application/json' },
      signal: AbortSignal.any([...(signal ? [signal] : []), AbortSignal.timeout(20000)]) });
    if (!response.ok) throw historyError(response.status,
      [401, 403].includes(response.status) ? 'provider_auth_rejected' : null);
    if (Number(response.headers.get('content-length')) > MAX_BYTES) throw historyError();
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let bytes = 0;
    let text = '';
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        bytes += value.byteLength;
        if (bytes > MAX_BYTES) { await reader.cancel(); throw historyError(); }
        text += decoder.decode(value, { stream: true });
      }
      text += decoder.decode();
    } finally { reader.releaseLock(); }
    const payload = JSON.parse(text);
    providerError(payload);
    return payload;
  } catch (error) {
    if (signal?.aborted) throw signal.reason || new DOMException('Aborted', 'AbortError');
    if (error.name === 'MarketDataRequestError') throw error;
    throw historyError(502, error instanceof TypeError ? 'provider_browser_unavailable' : null);
  }
}

function providerError(value) {
  const code = field(value, 'code');
  const status = Number(typeof code === 'object' ? field(code, 'value') : code);
  if (status === 401 || status === 403) throw historyError(status, 'provider_auth_rejected');
  const present = value => value != null && value !== false && value !== 0 && value !== '' &&
    (typeof value !== 'object' || Object.keys(value).length > 0);
  if (status || present(field(value, 'error')) || present(field(value, 'errors'))) throw historyError();
}

export function parseHistory(payload, grant, daily, start, end) {
  providerError(payload);
  const results = field(payload, 'results');
  if (!results || typeof results !== 'object') throw historyError();
  providerError(results);
  const candidates = oneOrMany(field(results, daily ? 'history' : 'intraday'));
  if (!candidates.length) {
    if (!daily || Number(field(results, 'symbolcount')) === 0 || Array.isArray(field(results, 'history'))) return [];
    throw historyError();
  }
  const history = candidates.find(item => String(field(item, 'symbolstring')).trim().toUpperCase() === grant.providerSymbol.toUpperCase());
  if (!history) throw historyError();
  providerError(history);
  if (daily && String(field(history, 'adjusted')).toLowerCase() === 'false') throw historyError();
  const entitlement = String(field(history, 'entitlement') || '').toUpperCase();
  if (entitlement && entitlement !== 'EOD' && (grant.availability === 'Historical' ||
      grant.availability === 'Delayed' && !entitlement.startsWith('DL'))) throw historyError();
  const rows = oneOrMany(field(history, daily ? 'eoddata' : 'interval'));
  const bars = new Map();
  let validRows = 0;
  let marketRows = 0;
  for (const row of rows) {
    providerError(row);
    if (!daily && field(row, 'type') && String(field(row, 'type')).toUpperCase() !== 'MKT') continue;
    marketRows++;
    const date = field(row, 'date');
    const timestamp = daily ? (/^\d{4}-\d{2}-\d{2}$/.test(date) ? exchangeMidnight(date, grant.timeZoneId) : NaN)
      : /(?:Z|[+-]\d{2}:?\d{2})$/i.test(field(row, 'startdatetime')) ? Date.parse(field(row, 'startdatetime')) : NaN;
    const price = key => {
      const value = !daily ? field(row, `unadjusted${key}`) ?? field(row, key) : field(row, key);
      return value == null || value === '' ? NaN : Number(value);
    };
    const bar = { timestampUtc: new Date(Number.isFinite(timestamp) ? timestamp : 0).toISOString(),
      open: price('open'), high: price('high'), low: price('low'), close: price('close'),
      volume: Math.max(0, Math.round(Number((daily ? field(row, 'avolume') : null) ?? field(row, 'sharevolume') ?? 0))) };
    if (!Number.isFinite(timestamp) || ![bar.open, bar.high, bar.low, bar.close, bar.volume].every(Number.isFinite) ||
        bar.open <= 0 || bar.close <= 0 || bar.low <= 0 || bar.high < Math.max(bar.open, bar.close) ||
        bar.low > Math.min(bar.open, bar.close)) continue;
    validRows++;
    const localDate = daily ? date : localParts(timestamp, grant.timeZoneId).date;
    if (timestamp > Date.now() || localDate < start || localDate > end) continue;
    bars.set(timestamp, bar);
  }
  if (marketRows && !validRows) throw historyError();
  return [...bars].sort(([a], [b]) => a - b).map(([, bar]) => bar);
}

function localParts(timestamp, timeZone) {
  if (!formatters.has(timeZone)) formatters.set(timeZone, new Intl.DateTimeFormat('en-CA', { timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    hourCycle: 'h23' }));
  const parts = Object.fromEntries(formatters.get(timeZone).formatToParts(new Date(timestamp)).map(part => [part.type, part.value]));
  return { date: `${parts.year}-${parts.month}-${parts.day}`, year: Number(parts.year), month: Number(parts.month),
    minute: Number(parts.hour) * 60 + Number(parts.minute) };
}

function exchangeMidnight(date, timeZone) {
  const utc = dateNumber(date);
  let timestamp = utc;
  for (let attempt = 0; attempt < 3; attempt++) {
    const local = localParts(timestamp, timeZone);
    timestamp += utc - (dateNumber(local.date) + local.minute * 60000);
  }
  return timestamp;
}

function combine(rows, timestamp = Date.parse(rows[0].timestampUtc)) {
  return { timestampUtc: new Date(timestamp).toISOString(), open: rows[0].open,
    high: Math.max(...rows.map(row => row.high)), low: Math.min(...rows.map(row => row.low)),
    close: rows.at(-1).close, volume: rows.reduce((sum, row) => sum + row.volume, 0) };
}

export function historyPage(bars, timeframe, grant, count, coverage = null) {
  const sourceTruncated = bars.length >= grant.sourceCount;
  const exhausted = coverage?.exhausted ?? !sourceTruncated;
  if (timeframe === grant.sourceTimeframe) return { bars, exhausted, nextEndDate: coverage?.nextEndDate || null };
  const match = /^(\d+)(m|h|D|W|M)$/.exec(timeframe);
  if (!match) throw historyError();
  const amount = Number(match[1]);
  const unit = match[2];
  let ordered = bars;
  const localDate = row => localParts(Date.parse(row.timestampUtc), grant.timeZoneId).date;
  if (sourceTruncated && ['m', 'h'].includes(unit) && bars.length && localDate(bars[0]) < localDate(bars.at(-1)))
    ordered = bars.filter(row => localDate(row) > localDate(bars[0]));
  const groups = new Map();
  for (const bar of ordered) {
    const local = localParts(Date.parse(bar.timestampUtc), grant.timeZoneId);
    const dayNumber = Math.floor(dateNumber(local.date) / DAY) + 719162;
    let bucket;
    if (unit === 'M') bucket = Math.floor((local.year * 12 + local.month - 1) / amount);
    else if (unit === 'W') bucket = Math.floor(Math.floor(dayNumber / 7) / amount);
    else if (unit === 'D') bucket = Math.floor(dayNumber / amount);
    else {
      const anchor = ['Stock', 'Etf', 'Index', 'Option'].includes(grant.securityType) ? 570 : 0;
      bucket = dayNumber * 4096 + Math.floor((local.minute - anchor) / (amount * (unit === 'h' ? 60 : 1)));
    }
    if (!groups.has(bucket)) groups.set(bucket, []);
    groups.get(bucket).push(bar);
  }
  let rows = [...groups.values()];
  if (sourceTruncated && ['D', 'W', 'M'].includes(unit) && rows.length > 1) rows = rows.slice(1);
  const result = rows.map(group => combine(group)).slice(-count);
  return { bars: result, exhausted, nextEndDate: coverage?.nextEndDate ||
    (!exhausted && result.length && result.length < count ? shift(localDate(result[0]), -1) : null) };
}
